import { useState, useEffect, useCallback } from 'react';
import { projectTypesAPI } from '../../utils/api';
import { PageHeader, Modal, FormField } from '../../components/layout/UI';

import { useToast, apiError } from '../../components/Toast';
import { Check, Eye, Plus, SquarePen, X } from 'lucide-react';

const PlusIc = () => <Plus size={14} />;
const PenIc = () => <SquarePen size={13} />;
const XIcon = () => <X size={13} />;

const CATEGORIES = ['draft','active','on_hold','completed','cancelled'];
const STATUS_COLORS_DEFAULT = ['#2E539F','#10b981','#f59e0b','#ef4444','#0891b2','#8b5cf6','#f97316','#94a3b8'];

const defaultForm = {
  name:'', code:'', icon:'📁', color:'#2E539F', description:'',
  statuses: [
    { name:'Open',        color:'#2E539F', category:'active',    is_default:true,  is_closed:false },
    { name:'In Progress', color:'#f59e0b', category:'active',    is_default:false, is_closed:false },
    { name:'On Hold',     color:'#94a3b8', category:'on_hold',   is_default:false, is_closed:false },
    { name:'Completed',   color:'#10b981', category:'completed', is_default:false, is_closed:true  },
    { name:'Cancelled',   color:'#ef4444', category:'cancelled', is_default:false, is_closed:true  },
  ],
  milestone_templates: [],
};

export default function AdminProjectTypesPage() {
  const toast = useToast();
  const [types,     setTypes]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [detailOpen,setDetailOpen]= useState(false);
  const [selected,  setSelected]  = useState(null);
  const [form,      setForm]      = useState({ ...defaultForm });
  const [saving,    setSaving]    = useState(false);
  const [editType,  setEditType]  = useState(null);
  const [confirmId, setConfirmId] = useState(null);
  const f = v => setForm(p => ({ ...p, ...v }));

  const load = useCallback(async () => {
    try { const r = await projectTypesAPI.getAll(); setTypes(r.data); }
    catch(e) { toast.error(apiError(e, 'Something went wrong loading this.')); } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openCreate = () => { setEditType(null); setForm({ ...defaultForm }); setModalOpen(true); };
  const openEdit   = (t) => {
    setEditType(t);
    setForm({ name:t.name, code:t.code, icon:t.icon, color:t.color, description:t.description||'',
              statuses: t.statuses||[], milestone_templates:[] });
    setModalOpen(true);
  };

  const handleSave = async () => {
    if (!form.name||!form.code) return;
    setSaving(true);
    try {
      if (editType) {
        await projectTypesAPI.update(editType.id, { name:form.name, icon:form.icon, color:form.color, description:form.description });
        toast.success('Item updated');
      } else {
        await projectTypesAPI.create(form);
        toast.success('Item created');
      }
      setModalOpen(false);
      load();
    } catch(e) { toast.error(apiError(e, 'Failed to save project type.')); }
    finally { setSaving(false); }
  };

  const toggleActive = async (t) => {
    try { await projectTypesAPI.update(t.id, { is_active: !t.is_active });
    toast.success('Item updated'); load(); }
    catch(e) { toast.error(apiError(e, `Failed to ${t.is_active?'deactivate':'activate'} project type`)); }
  };

  const addStatus = () => {
    const idx = form.statuses.length;
    f({ statuses: [...form.statuses, {
      name:'', color: STATUS_COLORS_DEFAULT[idx % STATUS_COLORS_DEFAULT.length],
      category:'active', is_default:false, is_closed:false
    }]});
  };

  const updateStatus = (i, field, val) => {
    const s = [...form.statuses];
    s[i] = { ...s[i], [field]: val };
    if (field==='is_default' && val) s.forEach((st,j) => { if(j!==i) s[j]={...s[j],is_default:false}; });
    f({ statuses: s });
  };

  const addMilestone = () => {
    f({ milestone_templates:[...form.milestone_templates,
      { name:'', sequence_no:form.milestone_templates.length, is_mandatory:true, due_offset_days:'', requires_approval:false }]});
  };

  const updateMilestone = (i, field, val) => {
    const m = [...form.milestone_templates];
    m[i] = { ...m[i], [field]:val };
    f({ milestone_templates:m });
  };

  const openDetail = async (t) => {
    const r = await projectTypesAPI.getById(t.id);
    setSelected(r.data);
    setDetailOpen(true);
  };

  return (
    <div>
      <PageHeader title="Project Types" subtitle="Configure project types, workflows, and milestone templates"
        actions={<button onClick={openCreate} className="btn-primary flex items-center gap-1.5 text-sm"><PlusIc /> New Project Type</button>}/>

      {loading ? (
        <div className="flex justify-center py-16"><div className="w-8 h-8 border-2 border-brand-500 border-t-transparent rounded-full animate-spin"/></div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {types.map(t => (
            <div key={t.id} className={`card transition-all ${!t.is_active ? 'opacity-60' : ''}`}>
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl flex-shrink-0"
                    style={{ background: t.color + '20', border: `2px solid ${t.color}20` }}>
                    {t.icon}
                  </div>
                  <div>
                    <p className="font-bold text-base" style={{ color:'var(--text)' }}>{t.name}</p>
                    <p className="text-xs font-mono" style={{ color:'var(--text-secondary)' }}>{t.code}</p>
                  </div>
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${t.is_active ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400' : 'bg-slate-100 text-slate-500 dark:bg-slate-800 dark:text-slate-400'}`}>
                  {t.is_active ? 'Active' : 'Inactive'}
                </span>
              </div>

              {t.description && <p className="text-xs mb-3 leading-relaxed" style={{ color:'var(--text-secondary)' }}>{t.description}</p>}

              {/* Status pills */}
              {t.statuses?.length > 0 && (
                <div className="flex flex-wrap gap-1 mb-3">
                  {t.statuses.slice(0,6).map((s,i) => (
                    <span key={i} className="text-xs px-2 py-0.5 rounded-full text-white font-medium"
                      style={{ background: s.color }}>
                      {s.name}
                    </span>
                  ))}
                  {t.statuses.length > 6 && <span className="text-xs px-2 py-0.5 rounded-full" style={{ background:'var(--surface-alt)', color:'var(--text-secondary)' }}>+{t.statuses.length-6}</span>}
                </div>
              )}

              <div className="flex items-center gap-3 text-xs pt-2 border-t" style={{ borderColor:'var(--border)', color:'var(--text-secondary)' }}>
                <span>📁 {t.project_count||0} projects</span>
                <span>🏁 {t.milestone_template_count||0} milestones</span>
              </div>

              <div className="flex gap-2 mt-3">
                <button onClick={() => openDetail(t)} className="btn-secondary flex-1 flex items-center justify-center gap-1 text-xs"><Eye size={13}/> View</button>
                <button onClick={() => openEdit(t)}   className="btn-secondary flex-1 flex items-center justify-center gap-1 text-xs"><PenIc /> Edit</button>
                <button onClick={() => toggleActive(t)} title={t.is_active ? 'Deactivate' : 'Activate'}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${t.is_active ? 'bg-red-50 text-red-600 hover:bg-red-100 dark:bg-red-900/20 dark:text-red-400' : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100 dark:bg-emerald-900/20 dark:text-emerald-400'}`}>
                  {t.is_active ? <XIcon/> : <Check size={13}/>}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Create / Edit Modal ──────────────────────────────────────────── */}
      <Modal isOpen={modalOpen} onClose={()=>setModalOpen(false)}
        title={editType ? `Edit: ${editType.name}` : 'New Project Type'} size="lg">
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Type Name" required>
              <input className="input" value={form.name} placeholder="e.g. Support Project"
                onChange={e=>f({name:e.target.value,code:e.target.value.toUpperCase().replace(/\s+/g,'_').slice(0,20)})}/>
            </FormField>
            <FormField label="Code" required>
              <input className="input font-mono" value={form.code} placeholder="e.g. SUPPORT"
                onChange={e=>f({code:e.target.value.toUpperCase().replace(/\s+/g,'').slice(0,20)})}/>
            </FormField>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <FormField label="Icon (emoji)">
              <input className="input text-2xl" value={form.icon} onChange={e=>f({icon:e.target.value.slice(0,4)})} maxLength={4}/>
            </FormField>
            <FormField label="Colour">
              <div className="flex gap-2 items-center">
                <input type="color" className="w-10 h-10 rounded-lg cursor-pointer border-0 p-0" value={form.color} onChange={e=>f({color:e.target.value})}/>
                <input className="input flex-1 font-mono" value={form.color} onChange={e=>f({color:e.target.value})}/>
              </div>
            </FormField>
          </div>
          <FormField label="Description">
            <textarea className="input" rows={2} value={form.description} onChange={e=>f({description:e.target.value})} placeholder="What is this project type used for?"/>
          </FormField>

          {/* Statuses */}
          {!editType && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="label mb-0">Workflow Statuses</label>
                <button onClick={addStatus} className="text-xs text-brand-500 hover:text-brand-700 flex items-center gap-1"><PlusIc /> Add Status</button>
              </div>
              <div className="space-y-2 max-h-52 overflow-y-auto border rounded-xl p-2" style={{ borderColor:'var(--border)' }}>
                {form.statuses.map((s,i) => (
                  <div key={i} className="grid grid-cols-12 gap-2 items-center">
                    <input type="color" className="col-span-1 w-8 h-8 rounded cursor-pointer border-0 p-0" value={s.color}
                      onChange={e=>updateStatus(i,'color',e.target.value)}/>
                    <input className="input col-span-4 text-sm py-1" value={s.name} placeholder="Status name"
                      onChange={e=>updateStatus(i,'name',e.target.value)}/>
                    <select className="select col-span-3 text-xs py-1" value={s.category}
                      onChange={e=>updateStatus(i,'category',e.target.value)}>
                      {CATEGORIES.map(c=><option key={c} value={c}>{c.replace('_',' ')}</option>)}
                    </select>
                    <label className="col-span-2 flex items-center gap-1 text-xs cursor-pointer" style={{ color:'var(--text-secondary)' }}>
                      <input type="checkbox" checked={s.is_default} onChange={e=>updateStatus(i,'is_default',e.target.checked)}/> Default
                    </label>
                    <button onClick={()=>f({statuses:form.statuses.filter((_,j)=>j!==i)})}
                      className="col-span-2 text-red-400 hover:text-red-600 text-xs">Remove</button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Milestone templates */}
          {!editType && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="label mb-0">Milestone Templates <span className="font-normal text-xs" style={{ color:'var(--text-secondary)' }}>(optional)</span></label>
                <button onClick={addMilestone} className="text-xs text-brand-500 hover:text-brand-700 flex items-center gap-1"><PlusIc /> Add Milestone</button>
              </div>
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {form.milestone_templates.map((m,i) => (
                  <div key={i} className="flex gap-2 items-center p-2 rounded-xl border" style={{ borderColor:'var(--border)', background:'var(--surface-alt)' }}>
                    <span className="text-xs font-bold w-5 text-center" style={{ color:'var(--text-secondary)' }}>{i+1}</span>
                    <input className="input flex-1 text-sm py-1" value={m.name} placeholder="Milestone name"
                      onChange={e=>updateMilestone(i,'name',e.target.value)}/>
                    <input type="number" className="input w-20 text-sm py-1" value={m.due_offset_days} placeholder="Days"
                      onChange={e=>updateMilestone(i,'due_offset_days',e.target.value)} title="Days after project start"/>
                    <label className="flex items-center gap-1 text-xs cursor-pointer" style={{ color:'var(--text-secondary)' }}>
                      <input type="checkbox" checked={m.requires_approval} onChange={e=>updateMilestone(i,'requires_approval',e.target.checked)}/> Approval
                    </label>
                    <button onClick={()=>f({milestone_templates:form.milestone_templates.filter((_,j)=>j!==i)})}
                      className="text-red-400 text-xs hover:text-red-600">✕</button>
                  </div>
                ))}
                {form.milestone_templates.length===0 && (
                  <p className="text-xs text-center py-3" style={{ color:'var(--text-secondary)' }}>No milestone templates — project will start without auto-generated milestones</p>
                )}
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={()=>setModalOpen(false)} className="btn-secondary">Cancel</button>
            <button onClick={handleSave} disabled={saving||!form.name||!form.code} className="btn-primary flex items-center gap-2"
              style={form.name&&form.code?{background:form.color}:{}}>
              {saving&&<span className="w-3.5 h-3.5 border border-white/30 border-t-white rounded-full animate-spin"/>}
              {editType ? 'Update Type' : 'Create Type'}
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Detail View Modal ────────────────────────────────────────────── */}
      {selected && (
        <Modal isOpen={detailOpen} onClose={()=>setDetailOpen(false)} title={`${selected.icon} ${selected.name}`} size="lg">
          <div className="space-y-5">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div><span className="label text-xs">Code</span><p className="font-mono font-bold" style={{ color:'var(--text)' }}>{selected.code}</p></div>
              <div><span className="label text-xs">Status</span><p style={{ color: selected.is_active?'#10b981':'#94a3b8' }}>{selected.is_active?'Active':'Inactive'}</p></div>
              {selected.description && <div className="col-span-2"><span className="label text-xs">Description</span><p style={{ color:'var(--text)' }}>{selected.description}</p></div>}
            </div>

            {selected.statuses?.length > 0 && (
              <div>
                <p className="label text-xs mb-2">Workflow Statuses ({selected.statuses.length})</p>
                <div className="space-y-1">
                  {selected.statuses.map((s,i) => (
                    <div key={i} className="flex items-center gap-3 p-2 rounded-lg" style={{ background:'var(--surface-alt)' }}>
                      <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ background:s.color }}/>
                      <span className="text-sm font-medium flex-1" style={{ color:'var(--text)' }}>{s.name}</span>
                      <span className="text-xs" style={{ color:'var(--text-secondary)' }}>{s.category.replace('_',' ')}</span>
                      {s.is_default && <span className="text-xs text-brand-500 font-medium">Default</span>}
                      {s.is_closed  && <span className="text-xs text-slate-400">Closed</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {selected.milestone_templates?.length > 0 && (
              <div>
                <p className="label text-xs mb-2">Milestone Templates ({selected.milestone_templates.length})</p>
                <div className="space-y-1">
                  {selected.milestone_templates.map((m,i) => (
                    <div key={i} className="flex items-center gap-3 p-2 rounded-lg" style={{ background:'var(--surface-alt)' }}>
                      <span className="w-6 h-6 rounded flex items-center justify-center text-xs font-bold text-white flex-shrink-0" style={{ background:selected.color }}>{m.sequence_no+1}</span>
                      <span className="text-sm flex-1" style={{ color:'var(--text)' }}>{m.name}</span>
                      {m.due_offset_days && <span className="text-xs" style={{ color:'var(--text-secondary)' }}>Day {m.due_offset_days}</span>}
                      {m.requires_approval && <span className="text-xs text-amber-500 font-medium">Approval req.</span>}
                      {m.is_mandatory && <span className="text-xs text-red-400">Required</span>}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}
